import { INavigatorRepository } from "../../adapters/driven/repositories/navigatorRepository";

// APIProvider.GET swallows HTTP errors and returns the response body as data,
// so we detect error responses (e.g. 404) by inspecting the payload shape.
function isErrorResponse(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  return "detail" in (data as Record<string, unknown>);
}

export const NavigatorServiceImplFactory = ({
  navigatorRepository,
}: {
  navigatorRepository: INavigatorRepository;
}) => {
  // Helper to clear transcript localStorage for a given clinic + patient + visitNoteId
  const clearTranscriptLocalStorage = (
    clinicId: number,
    patientId: number,
    visitNoteId: number
  ) => {
    try {
      const storageKey = `transcript_segments_${clinicId}_${patientId}_${visitNoteId}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(
        "[IPV Service] Failed to clear transcript localStorage:",
        error
      );
    }
  };

  const createInitialVisit: ICreateInitialVisit = async ({
    patientId,
    clinicId,
    visitData,
  }) => {
    const payload = {
      clinic_id: clinicId,
      patient_id: patientId,
      type: visitData.type,
      shortlisted: visitData.shortlisted,
      notes: visitData.notes,
      file_link: visitData.file_link,
      total_time: visitData.total_time || 0,
      context: visitData.context || {},
      doc_type: visitData.doc_type || "np_visit", // NEW: Default to np_visit for backward compatibility
    };

    const response = await navigatorRepository.createInitialVisitAPI(payload);

    // Clear transcript localStorage ONLY if this is a final submission (type === 1, not draft)
    // and ONLY after successful save
    if (visitData.type === 1 && response.data?.ipv_note_id) {
      clearTranscriptLocalStorage(
        clinicId,
        patientId,
        response.data.ipv_note_id
      );
    }

    return {
      success: true,
      data: response.data,
    };
  };

  const getIPVNotesByStatus: IGetIPVNotesByStatus = async ({
    clinicId,
    status,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByStatusAPI({
        clinic_id: clinicId,
        status,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByStatusv3: IGetIPVNotesByStatusv3 = async ({
    clinicId,
    status,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByStatusAPIv3({
        clinic_id: clinicId,
        status,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const putIPVNote: IPutIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
    visitData,
  }) => {
    try {
      const payload = {
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
        type: visitData.type,
        shortlisted: visitData.shortlisted,
        notes: visitData.notes,
        file_link: visitData.file_link,
        total_time: visitData.total_time,
        context: visitData.context || {},
        doc_type: visitData.doc_type || "np_visit", // NEW: Default to np_visit for backward compatibility
      };

      const response = await navigatorRepository.putIPVNoteAPI(payload);

      // Clear transcript localStorage ONLY if this is a final submission (type === 1, not draft)
      // and ONLY after successful save
      if (visitData.type === 1) {
        clearTranscriptLocalStorage(clinicId, patientId, ipvNoteId);
      }

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByPatientID: IGetIPVNotesByPatientID = async ({
    patientId,
    clinicId,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByPatientIDAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      // Backend returns null if no note exists, or IInitialPatientVisit2 object
      return {
        success: true,
        data: response || undefined, // Convert null to undefined for consistent typing
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const deleteIPVNote: IDeleteIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
  }) => {
    try {
      const response = await navigatorRepository.deleteIPVNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const deleteIPVDraftAndSession: IDeleteIPVDraftAndSession = async ({
    patientId,
    clinicId,
    ipvNoteId,
    sessionId,
  }) => {
    try {
      const response = await navigatorRepository.deleteIPVDraftAndSessionAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
        session_id: sessionId,
      });

      // Clear transcript localStorage after successful deletion
      if (ipvNoteId) {
        clearTranscriptLocalStorage(clinicId, patientId, ipvNoteId);
      }

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientsForNavigator: IGetPatientsForNavigator = async ({
    clinicId,
  }) => {
    try {
      const response = await navigatorRepository.getPatientsForNavigatorAPI({
        clinic_id: clinicId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getNavigatorPatientById: IGetNavigatorPatientById = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await navigatorRepository.getNavigatorPatientByIdAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      if (isErrorResponse(response)) {
        return {
          success: false,
          error: {
            message: (response as any)?.detail?.message ?? "Patient not found",
          },
        };
      }
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updatePatientExtraInfo: IUpdatePatientExtraInfo = async ({
    patientId,
    clinicId,
    extraInfo,
  }) => {
    try {
      const response = await navigatorRepository.updatePatientExtraInfoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        extra_info: extraInfo,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientVisitNotes: IGetPatientVisitNotes = async ({
    clinic_id,
    patient_id,
  }) => {
    try {
      const response = await navigatorRepository.getPatientVisitNotesAPI({
        clinic_id: clinic_id,
        patient_id: patient_id,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      };
    }
  };

  const storePreChartNote: IStorePreChartNote = async ({
    clinicId,
    patientId,
    apptNote,
  }) => {
    try {
      const response = await navigatorRepository.storePreChartNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        apptNote: apptNote,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getAtRiskReviewLockStatus = async ({
    clinicId,
  }: {
    clinicId: number;
  }): Promise<{
    success: boolean;
    data?: IAtRiskReviewLockStatus;
    error?: { message: string };
  }> => {
    try {
      const response = await navigatorRepository.getAtRiskReviewLockStatusAPI({
        clinic_id: clinicId,
      });
      return {
        success: true,
        data: response as IAtRiskReviewLockStatus,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const postAtRiskReviewOverrideRequest = async ({
    clinicId,
    reasons,
  }: {
    clinicId: number;
    reasons: { patient_id: number; reason: string }[];
  }): Promise<{
    success: boolean;
    data?: { message: string; request_id: string };
    error?: { message: string };
  }> => {
    try {
      const response =
        await navigatorRepository.postAtRiskReviewOverrideRequestAPI({
          clinic_id: clinicId,
          reasons,
        });
      return {
        success: true,
        data: response as { message: string; request_id: string },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const patchAtRiskReviewOverrideRequest = async ({
    clinicId,
    reasons,
  }: {
    clinicId: number;
    reasons: { patient_id: number; reason: string }[];
  }): Promise<{
    success: boolean;
    error?: { message: string };
  }> => {
    try {
      await navigatorRepository.patchAtRiskReviewOverrideRequestAPI({
        clinic_id: clinicId,
        reasons,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const patchNavigatorIpvIneligible: IPatchNavigatorIpvIneligible = async ({
    clinicId,
    patientId,
    admissionId,
    navigatorIpvIneligible,
    isExcluded,
    excludeReason,
  }) => {
    try {
      await navigatorRepository.patchNavigatorIpvIneligibleAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        admission_id: admissionId,
        ...(navigatorIpvIneligible !== undefined && {
          navigator_ipv_ineligible: navigatorIpvIneligible,
        }),
        ...(isExcluded !== undefined && { is_excluded: isExcluded }),
        ...(excludeReason !== undefined && { exclude_reason: excludeReason }),
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    createInitialVisit,
    getIPVNotesByStatus,
    getIPVNotesByStatusv3,
    putIPVNote,
    getIPVNotesByPatientID,
    deleteIPVNote,
    deleteIPVDraftAndSession,
    getPatientsForNavigator,
    getNavigatorPatientById,
    updatePatientExtraInfo,
    getPatientVisitNotes,
    storePreChartNote,
    getAtRiskReviewLockStatus,
    postAtRiskReviewOverrideRequest,
    patchAtRiskReviewOverrideRequest,
    patchNavigatorIpvIneligible,
  };
};
